import {useEffect, useRef, useState} from 'react'
import cn from 'classnames'
import styles from './image-attachment.module.scss'

const MAX_BYTES = 5 * 1024 * 1024

/**
 * Single image slot. Has four visual states: empty (add), preview, uploading, error.
 *
 * @param {{
 *   value?: File|null,
 *   previewUrl?: string,
 *   status?: 'empty'|'preview'|'uploading'|'error',
 *   progress?: number,
 *   error?: string,
 *   onPick?: (file: File) => void,
 *   onRemove?: () => void,
 *   ariaLabel?: string
 * }} props
 */
export function ImageAttachment({
    value = null,
    previewUrl,
    status = value ? 'preview' : 'empty',
    progress = 0,
    error,
    onPick,
    onRemove,
    ariaLabel = 'Add photo'
}) {
    const inputRef = useRef(null)
    const [objectUrl, setObjectUrl] = useState(null)

    useEffect(() => {
        if (previewUrl || !value) {
            setObjectUrl(null)
            return undefined
        }
        const url = URL.createObjectURL(value)
        setObjectUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [value, previewUrl])

    const handleFileChange = (event) => {
        const file = event.target.files && event.target.files[0]
        if (file && onPick)
            onPick(file)
        event.target.value = ''
    }

    if (status === 'empty')
        return (
            <button
                type="button"
                className={cn(styles.slot, styles.add)}
                onClick={() => inputRef.current?.click()}
                aria-label={ariaLabel}
            >
                <span className={styles.plus} aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.25">
                        <path d="M12 5v14M5 12h14"/>
                    </svg>
                </span>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className={styles.fileInput}
                    onChange={handleFileChange}
                />
            </button>
        )

    const thumb = previewUrl || objectUrl

    return (
        <div
            className={cn(styles.slot, {
                [styles.previewState]: status === 'preview',
                [styles.uploading]: status === 'uploading',
                [styles.errorState]: status === 'error'
            })}
            aria-busy={status === 'uploading' || undefined}
        >
            {thumb && (
                <img src={thumb} alt="" className={styles.thumb}/>
            )}
            {status === 'uploading' && (
                <div className={styles.progressWrap} aria-label={`Uploading ${Math.round(progress * 100)}%`}>
                    <div className={styles.progressBar} style={{width: `${Math.min(100, Math.round(progress * 100))}%`}}/>
                </div>
            )}
            {status === 'error' && (
                <p className={styles.errorText}>{error || 'Upload failed'}</p>
            )}
            {onRemove && status !== 'uploading' && (
                <button
                    type="button"
                    className={styles.remove}
                    onClick={onRemove}
                    aria-label="Remove attachment"
                >
                    ×
                </button>
            )}
        </div>
    )
}

/**
 * Validates a file as image candidate. Returns {ok:true} or {ok:false, reason}.
 * @param {File} file
 */
export function validateImageFile(file) {
    if (!file || !file.type || !file.type.startsWith('image/'))
        return {ok: false, reason: 'Not an image'}
    if (file.size > MAX_BYTES)
        return {ok: false, reason: 'Image larger than 5 MB'}
    return {ok: true}
}
